(() => {
  const THEME_EVENT_PREFIX = 'theme:';

  const matchesSelector = (node, selector) =>
    node instanceof Element && node.matches(selector);

  const queryWithinRoot = (root, selector) => {
    const nodes = [];
    if (matchesSelector(root, selector)) {
      nodes.push(root);
    }
    if (root instanceof Element || root instanceof Document) {
      nodes.push(...root.querySelectorAll(selector));
    }
    return nodes;
  };

  const ThemeEvents = {
    emit(target, name, detail = {}, options = {}) {
      if (!name.startsWith(THEME_EVENT_PREFIX)) {
        throw new Error(`Theme event "${name}" must use the "theme:" namespace.`);
      }
      const event = new CustomEvent(name, {
        bubbles: true,
        cancelable: options.cancelable === true,
        detail
      });
      return target.dispatchEvent(event);
    },
    on(target, name, handler, options) {
      target.addEventListener(name, handler, options);
    },
    off(target, name, handler, options) {
      target.removeEventListener(name, handler, options);
    }
  };

  class ControllerRegistry {
    constructor() {
      this.controllers = new Map();
    }

    register(name, controller) {
      if (!controller || typeof controller.init !== 'function' || typeof controller.destroy !== 'function' || typeof controller.refresh !== 'function') {
        throw new Error(`Controller "${name}" must implement init(), destroy(), and refresh().`);
      }
      this.controllers.set(name, controller);
    }

    init(root = document) {
      this.controllers.forEach((controller) => controller.init(root));
    }

    destroy(root = document) {
      this.controllers.forEach((controller) => controller.destroy(root));
    }

    refresh(root = document) {
      this.controllers.forEach((controller) => controller.refresh(root));
    }
  }

  class DropdownController {
    constructor(events) {
      this.events = events;
      this.selector = '[data-bs-toggle="dropdown"]';
      this.instances = new WeakMap();
      this.instanceToggles = new Set();
      this.documentListenersBound = false;

      this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.handleDocumentEscape = this.handleDocumentEscape.bind(this);
    }

    init(root = document) {
      queryWithinRoot(root, this.selector).forEach((toggle) => this.createInstance(toggle));
      this.bindDocumentListeners();
    }

    destroy(root = document) {
      queryWithinRoot(root, this.selector).forEach((toggle) => this.destroyInstance(toggle));
      if (this.instanceToggles.size === 0) {
        this.unbindDocumentListeners();
      }
    }

    refresh(root = document) {
      this.destroy(root);
      this.init(root);
    }

    createInstance(toggle) {
      if (!(toggle instanceof HTMLElement) || this.instances.has(toggle)) {
        return;
      }

      const dropdown = toggle.closest('.dropdown, .dropup, .dropstart, .dropend, .dropdown-center, .dropup-center') || toggle.parentElement;
      const menu = this.resolveMenu(toggle, dropdown);
      if (!menu) {
        return;
      }

      const instance = {
        toggle,
        menu,
        dropdown,
        onToggleClick: (event) => this.handleToggleClick(event, toggle),
        onToggleKeydown: (event) => this.handleToggleKeydown(event, toggle),
        onMenuKeydown: (event) => this.handleMenuKeydown(event, toggle),
        onMenuClick: (event) => this.handleMenuClick(event, toggle)
      };

      this.instances.set(toggle, instance);
      this.instanceToggles.add(toggle);

      toggle.addEventListener('click', instance.onToggleClick);
      toggle.addEventListener('keydown', instance.onToggleKeydown);
      menu.addEventListener('keydown', instance.onMenuKeydown);
      menu.addEventListener('click', instance.onMenuClick);

      if (!toggle.hasAttribute('aria-expanded')) {
        toggle.setAttribute('aria-expanded', 'false');
      }
      if (!toggle.hasAttribute('aria-haspopup')) {
        toggle.setAttribute('aria-haspopup', 'true');
      }
      if (menu.id && !toggle.hasAttribute('aria-controls')) {
        toggle.setAttribute('aria-controls', menu.id);
      }
      if (toggle.id && !menu.hasAttribute('aria-labelledby')) {
        menu.setAttribute('aria-labelledby', toggle.id);
      }
    }

    destroyInstance(toggle) {
      const instance = this.instances.get(toggle);
      if (!instance) {
        return;
      }

      instance.toggle.removeEventListener('click', instance.onToggleClick);
      instance.toggle.removeEventListener('keydown', instance.onToggleKeydown);
      instance.menu.removeEventListener('keydown', instance.onMenuKeydown);
      instance.menu.removeEventListener('click', instance.onMenuClick);

      this.close(instance, { reason: 'destroy' });

      this.instances.delete(toggle);
      this.instanceToggles.delete(toggle);
    }

    bindDocumentListeners() {
      if (this.documentListenersBound) {
        return;
      }
      document.addEventListener('pointerdown', this.handleDocumentPointerDown);
      document.addEventListener('keydown', this.handleDocumentEscape);
      this.documentListenersBound = true;
    }

    unbindDocumentListeners() {
      if (!this.documentListenersBound) {
        return;
      }
      document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
      document.removeEventListener('keydown', this.handleDocumentEscape);
      this.documentListenersBound = false;
    }

    resolveMenu(toggle, dropdown) {
      const target = toggle.getAttribute('data-bs-target');
      if (target && target.startsWith('#')) {
        const menuByTarget = document.querySelector(target);
        if (menuByTarget) {
          return menuByTarget;
        }
      }

      if (dropdown) {
        const menuInDropdown = dropdown.querySelector('.dropdown-menu');
        if (menuInDropdown) {
          return menuInDropdown;
        }
      }

      const sibling = toggle.nextElementSibling;
      if (sibling && sibling.classList.contains('dropdown-menu')) {
        return sibling;
      }

      return null;
    }

    handleToggleClick(event, toggle) {
      const instance = this.instances.get(toggle);
      if (!instance) {
        return;
      }

      // Prevent Bootstrap delegated dropdown runtime from handling this trigger.
      event.preventDefault();
      event.stopPropagation();

      this.toggle(instance, { reason: 'click', sourceEvent: event });
    }

    handleToggleKeydown(event, toggle) {
      const instance = this.instances.get(toggle);
      if (!instance) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close(instance, { reason: 'escape', sourceEvent: event });
        toggle.focus();
        return;
      }

      if (event.key === ' ' || event.key === 'Enter' || event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        event.stopPropagation();
        this.open(instance, { reason: 'keyboard', sourceEvent: event });
        if (event.key === 'ArrowUp' || event.key === 'End') {
          this.focusLastMenuItem(instance.menu);
        } else {
          this.focusFirstMenuItem(instance.menu);
        }
      }
    }

    handleMenuKeydown(event, toggle) {
      const instance = this.instances.get(toggle);
      if (!instance) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close(instance, { reason: 'escape', sourceEvent: event });
        toggle.focus();
        return;
      }

      const items = this.getMenuItems(instance.menu);
      if (items.length === 0) {
        return;
      }

      const currentIndex = items.indexOf(document.activeElement);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % items.length;
        this.focusMenuItem(items, nextIndex);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        const previousIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
        this.focusMenuItem(items, previousIndex);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        event.stopPropagation();
        this.focusMenuItem(items, 0);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        event.stopPropagation();
        this.focusMenuItem(items, items.length - 1);
      }
    }

    handleMenuClick(event, toggle) {
      const instance = this.instances.get(toggle);
      if (!instance) {
        return;
      }

      const item = event.target.closest('.dropdown-item, [role="menuitem"], [role="option"]');
      if (!item || !instance.menu.contains(item)) {
        return;
      }

      // Keep event propagation so other controllers/scripts (for example pill tabs)
      // continue to receive click semantics during migration.
      this.close(instance, { reason: 'item-select', sourceEvent: event });
    }

    handleDocumentPointerDown(event) {
      this.instanceToggles.forEach((toggle) => {
        const instance = this.instances.get(toggle);
        if (!instance || !this.isOpen(instance)) {
          return;
        }

        const insideMenu = instance.menu.contains(event.target);
        const insideToggle = instance.toggle.contains(event.target);
        if (!insideMenu && !insideToggle) {
          this.close(instance, { reason: 'outside-click', sourceEvent: event });
        }
      });
    }

    handleDocumentEscape(event) {
      if (event.key !== 'Escape') {
        return;
      }

      this.instanceToggles.forEach((toggle) => {
        const instance = this.instances.get(toggle);
        if (instance && this.isOpen(instance)) {
          this.close(instance, { reason: 'escape', sourceEvent: event });
        }
      });
    }

    toggle(instance, metadata = {}) {
      if (this.isOpen(instance)) {
        this.close(instance, metadata);
      } else {
        this.open(instance, metadata);
      }
    }

    open(instance, metadata = {}) {
      if (this.isOpen(instance)) {
        return;
      }

      const detail = this.buildEventDetail(instance, metadata);
      const allowed = this.events.emit(instance.dropdown || instance.toggle, 'theme:dropdown:before-show', detail, { cancelable: true });
      if (!allowed) {
        return;
      }

      this.closeAllExcept(instance);

      instance.toggle.setAttribute('aria-expanded', 'true');
      instance.toggle.classList.add('show');
      instance.menu.classList.add('show');
      if (!instance.menu.hasAttribute('data-bs-popper')) {
        instance.menu.setAttribute('data-bs-popper', 'static');
      }

      this.events.emit(instance.dropdown || instance.toggle, 'theme:dropdown:show', detail);
      this.events.emit(instance.dropdown || instance.toggle, 'theme:dropdown:shown', detail);
    }

    close(instance, metadata = {}) {
      if (!this.isOpen(instance)) {
        return;
      }

      const detail = this.buildEventDetail(instance, metadata);
      const allowed = this.events.emit(instance.dropdown || instance.toggle, 'theme:dropdown:before-hide', detail, { cancelable: true });
      if (!allowed) {
        return;
      }

      instance.toggle.setAttribute('aria-expanded', 'false');
      instance.toggle.classList.remove('show');
      instance.menu.classList.remove('show');

      this.events.emit(instance.dropdown || instance.toggle, 'theme:dropdown:hide', detail);
      this.events.emit(instance.dropdown || instance.toggle, 'theme:dropdown:hidden', detail);
    }

    closeAllExcept(keepInstance) {
      this.instanceToggles.forEach((toggle) => {
        const instance = this.instances.get(toggle);
        if (instance && instance !== keepInstance && this.isOpen(instance)) {
          this.close(instance, { reason: 'switch-open' });
        }
      });
    }

    isOpen(instance) {
      return instance.menu.classList.contains('show');
    }

    getMenuItems(menu) {
      return Array.from(
        menu.querySelectorAll('.dropdown-item:not([disabled]):not(.disabled), [role="menuitem"]:not([disabled]), [role="option"]:not([disabled])')
      ).filter((item) => item instanceof HTMLElement);
    }

    focusMenuItem(items, index) {
      const item = items[index];
      if (item instanceof HTMLElement) {
        item.focus();
      }
    }

    focusFirstMenuItem(menu) {
      const items = this.getMenuItems(menu);
      this.focusMenuItem(items, 0);
    }

    focusLastMenuItem(menu) {
      const items = this.getMenuItems(menu);
      this.focusMenuItem(items, items.length - 1);
    }

    buildEventDetail(instance, metadata) {
      return {
        trigger: instance.toggle,
        menu: instance.menu,
        dropdown: instance.dropdown,
        reason: metadata.reason || null,
        sourceEvent: metadata.sourceEvent || null
      };
    }
  }

  class ModalController {
    constructor(events) {
      this.events = events;
      this.triggerSelector = '[data-bs-toggle="modal"]';
      this.dismissSelector = '[data-bs-dismiss="modal"]';
      this.triggers = new WeakMap();
      this.triggerSet = new Set();
      this.modals = new WeakMap();
      this.modalRefCount = new WeakMap();
      this.activeModal = null;
      this.activeTrigger = null;
      this.previousFocusedElement = null;
    }

    init(root = document) {
      queryWithinRoot(root, this.triggerSelector).forEach((trigger) => this.createTrigger(trigger));
    }

    destroy(root = document) {
      queryWithinRoot(root, this.triggerSelector).forEach((trigger) => this.destroyTrigger(trigger));
      if (this.activeModal && !document.contains(this.activeModal)) {
        this.activeModal = null;
        this.activeTrigger = null;
      }
    }

    refresh(root = document) {
      this.destroy(root);
      this.init(root);
    }

    createTrigger(trigger) {
      if (!(trigger instanceof HTMLElement) || this.triggers.has(trigger)) {
        return;
      }

      const modal = this.resolveModal(trigger);
      if (!modal) {
        return;
      }

      this.ensureModalInstance(modal);

      const instance = {
        trigger,
        modal,
        onClick: (event) => this.handleTriggerClick(event, trigger),
        onKeydown: (event) => this.handleTriggerKeydown(event, trigger)
      };

      this.triggers.set(trigger, instance);
      this.triggerSet.add(trigger);
      trigger.addEventListener('click', instance.onClick);
      trigger.addEventListener('keydown', instance.onKeydown);
      if (!trigger.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    }

    destroyTrigger(trigger) {
      const instance = this.triggers.get(trigger);
      if (!instance) {
        return;
      }

      trigger.removeEventListener('click', instance.onClick);
      trigger.removeEventListener('keydown', instance.onKeydown);
      this.triggers.delete(trigger);
      this.triggerSet.delete(trigger);

      const modal = instance.modal;
      const currentCount = this.modalRefCount.get(modal) || 0;
      const nextCount = Math.max(0, currentCount - 1);
      if (nextCount === 0) {
        this.destroyModalInstance(modal);
      } else {
        this.modalRefCount.set(modal, nextCount);
      }

      if (this.activeTrigger === trigger) {
        this.activeTrigger = null;
      }
    }

    ensureModalInstance(modal) {
      if (this.modals.has(modal)) {
        const count = this.modalRefCount.get(modal) || 0;
        this.modalRefCount.set(modal, count + 1);
        return;
      }

      const dismissButtons = Array.from(modal.querySelectorAll(this.dismissSelector));
      const dismissHandlers = new Map();
      dismissButtons.forEach((button) => {
        const handler = (event) => this.handleDismissClick(event, modal);
        button.addEventListener('click', handler);
        dismissHandlers.set(button, handler);
      });

      const instance = {
        modal,
        dismissHandlers,
        onBackdropClick: (event) => this.handleBackdropClick(event, modal),
        onKeydown: (event) => this.handleModalKeydown(event, modal)
      };

      modal.addEventListener('click', instance.onBackdropClick);
      modal.addEventListener('keydown', instance.onKeydown);

      if (!modal.hasAttribute('role')) {
        modal.setAttribute('role', 'dialog');
      }

      this.modals.set(modal, instance);
      this.modalRefCount.set(modal, 1);
    }

    destroyModalInstance(modal) {
      const instance = this.modals.get(modal);
      if (!instance) {
        return;
      }

      instance.dismissHandlers.forEach((handler, button) => {
        button.removeEventListener('click', handler);
      });
      modal.removeEventListener('click', instance.onBackdropClick);
      modal.removeEventListener('keydown', instance.onKeydown);
      this.modals.delete(modal);
      this.modalRefCount.delete(modal);

      if (this.activeModal === modal) {
        this.hideModal(modal, { reason: 'destroy' });
      }
    }

    resolveModal(trigger) {
      const target = trigger.getAttribute('data-bs-target');
      if (target && target.startsWith('#')) {
        return document.querySelector(target);
      }
      return null;
    }

    handleTriggerClick(event, trigger) {
      const instance = this.triggers.get(trigger);
      if (!instance) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.showModal(instance.modal, trigger, { reason: 'click', sourceEvent: event });
    }

    handleTriggerKeydown(event, trigger) {
      const instance = this.triggers.get(trigger);
      if (!instance) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        this.showModal(instance.modal, trigger, { reason: 'keyboard', sourceEvent: event });
      }
    }

    handleDismissClick(event, modal) {
      event.preventDefault();
      event.stopPropagation();
      this.hideModal(modal, { reason: 'dismiss', sourceEvent: event });
    }

    handleBackdropClick(event, modal) {
      if (event.target !== modal) {
        return;
      }
      event.stopPropagation();
      this.hideModal(modal, { reason: 'backdrop', sourceEvent: event });
    }

    handleModalKeydown(event, modal) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.hideModal(modal, { reason: 'escape', sourceEvent: event });
        return;
      }

      if (event.key === 'Tab') {
        this.handleTabTrap(event, modal);
      }
    }

    showModal(modal, trigger, metadata = {}) {
      if (!(modal instanceof HTMLElement)) {
        return;
      }
      if (this.activeModal && this.activeModal !== modal) {
        this.hideModal(this.activeModal, { reason: 'switch-open' });
      }
      if (this.activeModal === modal) {
        return;
      }

      const detail = this.buildEventDetail(modal, trigger, metadata);
      const allowed = this.events.emit(modal, 'theme:modal:before-show', detail, { cancelable: true });
      if (!allowed) {
        return;
      }

      this.previousFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.activeModal = modal;
      this.activeTrigger = trigger;

      modal.style.display = 'block';
      modal.removeAttribute('aria-hidden');
      modal.setAttribute('aria-modal', 'true');
      modal.classList.add('show');
      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', 'true');
      }
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';

      this.events.emit(modal, 'theme:modal:show', detail);
      this.focusInitial(modal);
      this.onTransitionEnd(modal, () => {
        this.events.emit(modal, 'theme:modal:shown', detail);
      });
    }

    hideModal(modal, metadata = {}) {
      if (!(modal instanceof HTMLElement) || this.activeModal !== modal) {
        return;
      }

      const detail = this.buildEventDetail(modal, this.activeTrigger, metadata);
      const allowed = this.events.emit(modal, 'theme:modal:before-hide', detail, { cancelable: true });
      if (!allowed) {
        return;
      }

      modal.classList.remove('show');
      modal.setAttribute('aria-hidden', 'true');
      modal.removeAttribute('aria-modal');
      if (this.activeTrigger instanceof HTMLElement) {
        this.activeTrigger.setAttribute('aria-expanded', 'false');
      }
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');

      this.events.emit(modal, 'theme:modal:hide', detail);
      this.onTransitionEnd(modal, () => {
        modal.style.display = 'none';
        this.restoreFocus();
        this.events.emit(modal, 'theme:modal:hidden', detail);
      });

      this.activeModal = null;
      this.activeTrigger = null;
    }

    getFocusableElements(modal) {
      const selectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(',');

      return Array.from(modal.querySelectorAll(selectors)).filter((element) => {
        if (!(element instanceof HTMLElement)) {
          return false;
        }
        return element.offsetParent !== null || element === document.activeElement;
      });
    }

    handleTabTrap(event, modal) {
      const focusable = this.getFocusableElements(modal);
      if (focusable.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    focusInitial(modal) {
      const focusable = this.getFocusableElements(modal);
      if (focusable.length > 0) {
        focusable[0].focus();
        return;
      }

      if (!modal.hasAttribute('tabindex')) {
        modal.setAttribute('tabindex', '-1');
      }
      modal.focus();
    }

    restoreFocus() {
      if (this.previousFocusedElement && document.contains(this.previousFocusedElement)) {
        this.previousFocusedElement.focus();
      }
      this.previousFocusedElement = null;
    }

    onTransitionEnd(element, callback) {
      const style = window.getComputedStyle(element);
      const parseTime = (value) => {
        if (!value) return 0;
        if (value.endsWith('ms')) return parseFloat(value);
        if (value.endsWith('s')) return parseFloat(value) * 1000;
        return 0;
      };

      const durations = style.transitionDuration.split(',').map((value) => parseTime(value.trim()));
      const delays = style.transitionDelay.split(',').map((value) => parseTime(value.trim()));
      const maxDuration = Math.max(0, ...durations.map((duration, index) => duration + (delays[index] || 0)));

      if (maxDuration === 0 || !element.classList.contains('fade')) {
        callback();
        return;
      }

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        element.removeEventListener('transitionend', onEnd);
        callback();
      };
      const onEnd = (event) => {
        if (event.target === element) {
          finish();
        }
      };

      element.addEventListener('transitionend', onEnd);
      window.setTimeout(finish, maxDuration + 30);
    }

    buildEventDetail(modal, trigger, metadata) {
      return {
        modal,
        trigger: trigger || null,
        reason: metadata.reason || null,
        sourceEvent: metadata.sourceEvent || null
      };
    }
  }

  class PillsController {
    constructor() {
      this.triggerSelector = '[data-bs-toggle="pill"], [data-bs-toggle="tab"]';
      this.triggerListeners = new WeakMap();
    }

    init(root = document) {
      queryWithinRoot(root, this.triggerSelector).forEach((trigger) => this.bindTrigger(trigger));
      this.syncState(root);
    }

    destroy(root = document) {
      queryWithinRoot(root, this.triggerSelector).forEach((trigger) => this.unbindTrigger(trigger));
    }

    refresh(root = document) {
      this.destroy(root);
      this.init(root);
    }

    bindTrigger(trigger) {
      if (!(trigger instanceof HTMLElement) || this.triggerListeners.has(trigger)) {
        return;
      }

      const panel = this.resolvePanel(trigger);
      if (!(panel instanceof HTMLElement)) {
        return;
      }

      const onClick = (event) => {
        // Prevent anchor jump while allowing other trigger-level listeners
        // to keep running (collection-tabs and meet-our-filters scripts).
        event.preventDefault();
        this.activateByTrigger(trigger);
      };

      this.triggerListeners.set(trigger, onClick);
      trigger.addEventListener('click', onClick);
    }

    unbindTrigger(trigger) {
      const onClick = this.triggerListeners.get(trigger);
      if (!onClick) {
        return;
      }

      trigger.removeEventListener('click', onClick);
      this.triggerListeners.delete(trigger);
    }

    resolvePanel(trigger) {
      const target = trigger.getAttribute('data-bs-target');
      if (!target || !target.startsWith('#')) {
        return null;
      }

      try {
        return document.querySelector(target);
      } catch (_error) {
        return null;
      }
    }

    activateByTrigger(trigger) {
      const panel = this.resolvePanel(trigger);
      if (!(panel instanceof HTMLElement)) {
        return;
      }

      const paneContainer = this.resolvePaneContainer(panel);
      if (!(paneContainer instanceof HTMLElement)) {
        return;
      }

      this.syncPanes(paneContainer, panel);
      this.syncTriggersForPaneContainer(paneContainer, panel);
    }

    syncState(root = document) {
      const paneContainers = new Set();
      queryWithinRoot(root, this.triggerSelector).forEach((trigger) => {
        const panel = this.resolvePanel(trigger);
        const paneContainer = panel ? this.resolvePaneContainer(panel) : null;
        if (paneContainer instanceof HTMLElement) {
          paneContainers.add(paneContainer);
        }
      });

      paneContainers.forEach((paneContainer) => {
        const activePane = this.resolveActivePane(paneContainer);
        this.syncPanes(paneContainer, activePane);
        this.syncTriggersForPaneContainer(paneContainer, activePane);
      });
    }

    resolvePaneContainer(panel) {
      if (!(panel instanceof HTMLElement)) {
        return null;
      }
      const paneContainer = panel.closest('.tab-content');
      return paneContainer instanceof HTMLElement ? paneContainer : null;
    }

    resolveActivePane(paneContainer) {
      const panes = Array.from(paneContainer.querySelectorAll('.tab-pane')).filter((pane) => pane instanceof HTMLElement);
      const activeByClass = panes.find((pane) => pane.classList.contains('active') || pane.classList.contains('show'));
      return activeByClass || null;
    }

    syncPanes(paneContainer, activePane) {
      paneContainer.querySelectorAll('.tab-pane').forEach((pane) => {
        if (!(pane instanceof HTMLElement)) {
          return;
        }
        const isActive = pane === activePane;
        pane.classList.toggle('active', isActive);
        pane.classList.toggle('show', isActive);
      });
    }

    syncTriggersForPaneContainer(paneContainer, activePane) {
      queryWithinRoot(document, this.triggerSelector).forEach((trigger) => {
        const panel = this.resolvePanel(trigger);
        if (!(panel instanceof HTMLElement)) {
          return;
        }
        if (this.resolvePaneContainer(panel) !== paneContainer) {
          return;
        }
        const isActive = panel === activePane;
        trigger.classList.toggle('active', isActive);
        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }
  }

  class CollapseController {
    constructor() {
      this.triggerSelector = '[data-bs-toggle="collapse"]';
      this.triggerListeners = new WeakMap();
      this.boundaryListener = (event) => this.handleDocumentCollapseBoundary(event);

      // Register early so this document-level listener runs before Bootstrap's
      // delegated collapse Data API listener (same event phase, first-registered wins).
      document.addEventListener('click', this.boundaryListener);
    }

    init(root = document) {
      queryWithinRoot(root, this.triggerSelector).forEach((trigger) => this.bindTrigger(trigger));
    }

    destroy(root = document) {
      queryWithinRoot(root, this.triggerSelector).forEach((trigger) => this.unbindTrigger(trigger));
    }

    refresh(root = document) {
      this.destroy(root);
      this.init(root);
    }

    bindTrigger(trigger) {
      if (!(trigger instanceof HTMLElement) || this.triggerListeners.has(trigger)) {
        return;
      }

      const panel = this.resolvePanel(trigger);
      if (!(panel instanceof HTMLElement)) {
        return;
      }

      const onClick = (event) => {
        this.traceCollapseStep('handleTriggerClick() enter', panel, 'preventDefault + togglePanel');
        event.preventDefault();
        this.togglePanel(panel);
      };
      this.triggerListeners.set(trigger, onClick);
      trigger.addEventListener('click', onClick);
      this.syncTriggerState(trigger, this.isPanelOpen(panel));
    }

    unbindTrigger(trigger) {
      const onClick = this.triggerListeners.get(trigger);
      if (!onClick) {
        return;
      }

      trigger.removeEventListener('click', onClick);
      this.triggerListeners.delete(trigger);
    }

    resolvePanel(trigger) {
      const target = trigger.getAttribute('data-bs-target');
      if (!target || !target.startsWith('#')) {
        return null;
      }

      try {
        return document.querySelector(target);
      } catch (_error) {
        return null;
      }
    }

    traceCollapseStep(step, panel, decision = '') {
      const panelId = panel instanceof HTMLElement ? (panel.id || '(no-id)') : '(invalid-panel)';
      const panelClassName = panel instanceof HTMLElement ? panel.className : '(invalid-panel)';
      const isOpen = panel instanceof HTMLElement ? panel.classList.contains('show') : false;
    }

    togglePanel(panel) {
      this.traceCollapseStep('togglePanel() enter', panel, 'evaluate isPanelOpen');
      const panelIsOpen = this.isPanelOpen(panel);
      const decision = panelIsOpen ? 'closePanel()' : 'openPanel()';
      this.traceCollapseStep('togglePanel() decision', panel, decision);

      if (panelIsOpen) {
        this.closePanel(panel);
      } else {
        this.openPanel(panel);
      }
    }

    openPanel(panel) {
      this.traceCollapseStep('openPanel() enter', panel, 'guard check');
      if (!(panel instanceof HTMLElement) || this.isPanelOpen(panel)) {
        this.traceCollapseStep('openPanel() early return', panel, 'panel invalid or already open');
        return;
      }

      this.traceCollapseStep('openPanel() apply open', panel, 'add .show + syncTriggersForPanel(true)');
      this.closeSiblingPanels(panel);
      panel.classList.add('show');
      this.syncTriggersForPanel(panel, true);
      this.traceCollapseStep('openPanel() exit', panel, 'opened');
    }

    closePanel(panel) {
      this.traceCollapseStep('closePanel() enter', panel, 'guard check');
      if (!(panel instanceof HTMLElement) || !this.isPanelOpen(panel)) {
        this.traceCollapseStep('closePanel() early return', panel, 'panel invalid or already closed');
        return;
      }

      this.traceCollapseStep('closePanel() apply close', panel, 'remove .show + syncTriggersForPanel(false)');
      panel.classList.remove('show');
      this.syncTriggersForPanel(panel, false);
      this.traceCollapseStep('closePanel() exit', panel, 'closed');
    }

    closeSiblingPanels(panel) {
      const parentSelector = panel.getAttribute('data-bs-parent');
      if (!parentSelector) {
        return;
      }

      const parent = document.querySelector(parentSelector);
      if (!parent) {
        return;
      }

      parent.querySelectorAll('.accordion-collapse.show').forEach((siblingPanel) => {
        if (!(siblingPanel instanceof HTMLElement) || siblingPanel === panel) {
          return;
        }

        this.closePanel(siblingPanel);
      });
    }

    syncTriggersForPanel(panel, expanded) {
      const panelId = panel.getAttribute('id');
      if (!panelId) {
        return;
      }

      const targetValue = `#${panelId}`;
      queryWithinRoot(document, this.triggerSelector).forEach((trigger) => {
        if (trigger.getAttribute('data-bs-target') !== targetValue) {
          return;
        }
        this.syncTriggerState(trigger, expanded);
      });
    }

    syncTriggerState(trigger, expanded) {
      trigger.classList.toggle('collapsed', !expanded);
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    isPanelOpen(panel) {
      const panelIsOpen = panel instanceof HTMLElement && panel.classList.contains('show');
      this.traceCollapseStep('isPanelOpen()', panel, panelIsOpen ? 'return true' : 'return false');
      return panelIsOpen;
    }

    handleDocumentCollapseBoundary(event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const trigger = target.closest(this.triggerSelector);
      if (!trigger) {
        return;
      }

      // Prevent Bootstrap's delegated collapse Data API listener on document
      // while allowing target-level handlers (including this controller) to run.
      event.stopImmediatePropagation();
    }
  }

  const registry = new ControllerRegistry();
  const dropdownController = new DropdownController(ThemeEvents);
  const modalController = new ModalController(ThemeEvents);
  const pillsController = new PillsController();
  const collapseController = new CollapseController();
  registry.register('dropdown', dropdownController);
  registry.register('modal', modalController);
  registry.register('pills', pillsController);
  registry.register('collapse', collapseController);

  const ThemeInteractions = {
    events: ThemeEvents,
    registry,
    init(root = document) {
      registry.init(root);
    },
    destroy(root = document) {
      registry.destroy(root);
    },
    refresh(root = document) {
      registry.refresh(root);
    }
  };

  window.ThemeInteractions = ThemeInteractions;

  const resolveSectionRoot = (event) => {
    if (event && event.target instanceof Element) {
      return event.target;
    }
    return document;
  };

  const bindSectionLifecycle = () => {
    document.addEventListener('shopify:section:load', (event) => {
      ThemeInteractions.refresh(resolveSectionRoot(event));
    });

    document.addEventListener('shopify:section:unload', (event) => {
      ThemeInteractions.destroy(resolveSectionRoot(event));
    });

    document.addEventListener('shopify:section:reorder', (event) => {
      ThemeInteractions.refresh(resolveSectionRoot(event));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      ThemeInteractions.init(document);
      bindSectionLifecycle();
    });
  } else {
    ThemeInteractions.init(document);
    bindSectionLifecycle();
  }
})();
